/**
 *
 *  Contact Us page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 21/04/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppState } from "../../state";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import Button from "../Button/Button";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import { WorkoutIcon } from "../PartnerProductBadge/PartnerProductBadge";
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Utils
import { clientWidth } from "../../utils";

// Images
import hero from "../../images/pillars-hero.jpg";
import hero2x from "../../images/pillars-hero@2x.jpg";
import heroMobile from "../../images/pillars-hero-mobile.jpg";
import heroMobile2x from "../../images/pillars-hero-mobile@2x.jpg";
import TruBeLogoVertical from "../AboutPage/TruBeLogoVertical";

import fitnessBg from "./fitness-bg.jpg";
import fitnessBg2x from "./fitness-bg@2x.jpg";
import fitnessBgMobile from "./fitness-bg-mobile.jpg";
import fitnessBgMobile2x from "./fitness-bg-mobile@2x.jpg";
import spiritBg from "./spirit-bg.jpg";
import spiritBg2x from "./spirit-bg@2x.jpg";
import spiritBgMobile from "./spirit-bg-mobile.jpg";
import spiritBgMobile2x from "./spirit-bg-mobile@2x.jpg";
import passionBg from "./passion-bg.jpg";
import passionBg2x from "./passion-bg@2x.jpg";
import passionBgMobile from "./passion-bg-mobile.jpg";
import passionBgMobile2x from "./passion-bg-mobile@2x.jpg";
import bodyBg from "./body-bg.jpg";
import bodyBg2x from "./body-bg@2x.jpg";
import bodyBgMobile from "./body-bg-mobile.jpg";
import bodyBgMobile2x from "./body-bg-mobile@2x.jpg";

import disciplineSliderPt from "../../images/homepage-discipline-slider-pt.jpg";
import disciplineSliderPt2x from "../../images/homepage-discipline-slider-pt@2x.jpg";
import disciplineSliderKickboxing from "../../images/homepage-discipline-slider-kickboxing.jpg";
import disciplineSliderKickboxing2x from "../../images/homepage-discipline-slider-kickboxing@2x.jpg";
import disciplineSliderPilates from "../../images/homepage-discipline-slider-pilates.jpg";
import disciplineSliderPilates2x from "../../images/homepage-discipline-slider-pilates@2x.jpg";
import disciplineSliderPrePostNatal from "../../images/homepage-discipline-slider-prepost-natal.jpg";
import disciplineSliderPrePostNatal2x from "../../images/homepage-discipline-slider-prepost-natal@2x.jpg";
import disciplineSliderBalletFit from "../../images/homepage-discipline-slider-ballet-fit.jpg";
import disciplineSliderBalletFit2x from "../../images/homepage-discipline-slider-ballet-fit@2x.jpg";
import disciplineSliderBoxing from "../../images/homepage-discipline-slider-boxing.jpg";
import disciplineSliderBoxing2x from "../../images/homepage-discipline-slider-boxing@2x.jpg";

import disciplineSliderYoga from "../../images/homepage-discipline-slider-yoga.jpg";
import disciplineSliderYoga2x from "../../images/homepage-discipline-slider-yoga@2x.jpg";
import disciplineSliderMeditation from "../../images/homepage-discipline-slider-meditation.jpg";
import disciplineSliderMeditation2x from "../../images/homepage-discipline-slider-meditation@2x.jpg";
import disciplineSliderSoundBaths from "../../images/homepage-discipline-slider-sound-baths.jpg";
import disciplineSliderSoundBaths2x from "../../images/homepage-discipline-slider-sound-baths@2x.jpg";
import disciplineSliderLifestyle from "../../images/homepage-discipline-slider-lifestyle-coaching.jpg";
import disciplineSliderLifestyle2x from "../../images/homepage-discipline-slider-lifestyle-coaching@2x.jpg";
import disciplineSliderStressManagement from "../../images/homepage-discipline-slider-stress-management.jpg";
import disciplineSliderStressManagement2x from "../../images/homepage-discipline-slider-stress-management@2x.jpg";
import disciplineSliderMotivation from "../../images/homepage-discipline-slider-motivational-coaching.jpg";
import disciplineSliderMotivation2x from "../../images/homepage-discipline-slider-motivational-coaching@2x.jpg";

import disciplineCardTennis from "../../images/discipline-card-tennis.jpg";
import disciplineCardTennis2x from "../../images/discipline-card-tennis@2x.jpg";
import disciplineCardGolf from "../../images/discipline-card-golf.jpg";
import disciplineCardGolf2x from "../../images/discipline-card-golf@2x.jpg";
import disciplineCardSoccer from "../../images/discipline-card-soccer.jpg";
import disciplineCardSoccer2x from "../../images/discipline-card-soccer@2x.jpg";
import disciplineCardBasketball from "../../images/discipline-card-basketball.jpg";
import disciplineCardBasketball2x from "../../images/discipline-card-basketball@2x.jpg";
import disciplineCardPickleball from "../../images/discipline-card-pickleball.jpg";
import disciplineCardPickleball2x from "../../images/discipline-card-pickleball@2x.jpg";
import disciplineCardBaseball from "../../images/discipline-card-baseball.jpg";
import disciplineCardBaseball2x from "../../images/discipline-card-baseball@2x.jpg";

import pillarsVitamins from "../../images/pillars-vitamins.jpg";
import pillarsVitamins2x from "../../images/pillars-vitamins.jpg";
import pillarsSliderSupplements from "../../images/pillars-supplements.jpg";
import pillarsSliderSupplements2x from "../../images/pillars-supplements.jpg";
import pillarsIVInfusion from "../../images/pillars-iv-infusion.jpg";
import pillarsIVInfusion2x from "../../images/pillars-iv-infusion.jpg";
import pillarsNutrition from "../../images/pillars-nutrition.jpg";
import pillarsNutrition2x from "../../images/pillars-nutrition.jpg";
import pillarsMealPlanning from "../../images/pillars-meal-planning.jpg";
import pillarsMealPlanning2x from "../../images/pillars-meal-planning@2x.jpg";
import pillarsDetoxAndCleanses from "../../images/pillars-detox-and-cleanses.jpg";
import pillarsDetoxAndCleanses2x from "../../images/pillars-detox-and-cleanses@2x.jpg";

const mobileSliderFitnessRows = [
  [
    {
      link: "/partners/personal-training",
      title: "Personal Training",
      images: [disciplineSliderPt, disciplineSliderPt2x],
    },
    {
      link: "/partners/kickboxing",
      title: "Kickboxing",
      images: [disciplineSliderKickboxing, disciplineSliderKickboxing2x],
    },
    {
      link: "/partners/pilates",
      title: "Pilates",
      images: [disciplineSliderPilates, disciplineSliderPilates2x],
    },
  ],
  [
    {
      link: "/partners/prepost-natal",
      title: "Pre/ Post Natal",
      images: [disciplineSliderPrePostNatal, disciplineSliderPrePostNatal2x],
    },
    {
      link: "/partners/ballet-fit",
      title: "Ballet Fit",
      images: [disciplineSliderBalletFit, disciplineSliderBalletFit2x],
    },
    {
      link: "/partners/boxing",
      title: "Boxing",
      images: [disciplineSliderBoxing, disciplineSliderBoxing2x],
    },
  ],
];

const mobileSliderSpiritRows = [
  [
    {
      link: "/partners/yoga",
      title: "Yoga",
      images: [disciplineSliderYoga, disciplineSliderYoga2x],
    },
    {
      link: "/partners/meditation",
      title: "Meditation",
      images: [disciplineSliderMeditation, disciplineSliderMeditation2x],
    },
    {
      link: "/partners/sound-baths",
      title: "Sound Baths",
      images: [disciplineSliderSoundBaths, disciplineSliderSoundBaths2x],
    },
  ],
  [
    {
      link: "/partners/lifestyle",
      title: "Lifestyle",
      images: [disciplineSliderLifestyle, disciplineSliderLifestyle2x],
    },
    {
      link: "/partners/stress-management",
      title: "Stress Management",
      images: [
        disciplineSliderStressManagement,
        disciplineSliderStressManagement2x,
      ],
    },
    {
      link: "/partners/motivation",
      title: "Motivation",
      images: [disciplineSliderMotivation, disciplineSliderMotivation2x],
    },
  ],
];

const mobileSliderPassionRows = [
  [
    {
      // link: "/partners/tennis",
      title: "Tennis",
      images: [disciplineCardTennis, disciplineCardTennis2x],
    },
    {
      // link: "/partners/golf",
      title: "Golf",
      images: [disciplineCardGolf, disciplineCardGolf2x],
    },
    {
      // link: "/partners/soccer",
      title: "Soccer",
      images: [disciplineCardSoccer, disciplineCardSoccer2x],
    },
  ],
  [
    {
      // link: "/partners/basketball",
      title: "Basketball",
      images: [disciplineCardBasketball, disciplineCardBasketball2x],
    },
    {
      // link: "/partners/pickleball",
      title: "Pickleball",
      images: [disciplineCardPickleball, disciplineCardPickleball2x],
    },
    {
      // link: "/partners/baseball",
      title: "Baseball",
      images: [disciplineCardBaseball, disciplineCardBaseball2x],
    },
  ],
];

const mobileSliderBodyRows = [
  [
    {
      // link: "/partners/vitamins",
      title: "Vitamins",
      images: [pillarsVitamins, pillarsVitamins2x],
    },
    {
      // link: "/partners/supplements",
      title: "Supplements",
      images: [pillarsSliderSupplements, pillarsSliderSupplements2x],
    },
    {
      // link: "/partners/iv-infusion",
      title: "IV Infusion",
      images: [pillarsIVInfusion, pillarsIVInfusion2x],
    },
  ],
  [
    {
      // link: "/partners/nutrition",
      title: "Nutrition",
      images: [pillarsNutrition, pillarsNutrition2x],
    },
    {
      // link: "/partners/meal-planning",
      title: "Meal Planning",
      images: [pillarsMealPlanning, pillarsMealPlanning2x],
    },
    {
      // link: "/partners/detox-and-cleanses",
      title: "Detox & Cleanses",
      images: [pillarsDetoxAndCleanses, pillarsDetoxAndCleanses2x],
    },
  ],
];

const DisciplineCard = ({ link, title, images, hideIcon }) =>
  link ? (
    <Link to={link}>
      <div className="tbk-relative tbk-pt-[90.856467%]">
        <img
          className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-rounded-2xl tbk-object-cover"
          src={images[0]}
          srcSet={`${images[0]} 1x, ${images[1]} 2x`}
          alt={title}
        />
      </div>
      <div className="tbk-flex tbk-items-center tbk-py-2 tbk-text-grey-main">
        {!hideIcon ? <WorkoutIcon label={title} size={32} /> : null}
        <div className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-min-w-0 tbk-grow tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap">
          {title}
        </div>
        <ArrowRightIcon className="tbk-shrink-0" />
      </div>
    </Link>
  ) : (
    <div>
      <div className="tbk-relative tbk-pt-[90.856467%]">
        <img
          className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-rounded-2xl tbk-object-cover"
          src={images[0]}
          srcSet={`${images[0]} 1x, ${images[1]} 2x`}
          alt={title}
        />
      </div>
      <div className="tbk-flex tbk-items-center tbk-py-2 tbk-text-grey-main">
        {!hideIcon ? <WorkoutIcon label={title} size={32} /> : null}
        <div className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-min-w-0 tbk-grow tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap">
          {title}
        </div>
      </div>
    </div>
  );

export default function PillarsPage() {
  const { theme } = useTheme();
  const { hash } = useLocation();

  const { user } = useAppState();

  const heroRef = React.useRef(null);

  React.useEffect(() => {
    if (!hash) {
      return;
    }

    const element = document.getElementById(hash.replace("#", ""));

    if (element) {
      // 👇 Will scroll smoothly to the top of the next section
      element.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  return (
    <>
      <ScrollToTopOnMount />
      <Header
        behaviour="none"
        isFixed
        isLoggedIn={!!(user && user.id)}
        heroRef={heroRef}
      />

      <div
        ref={heroRef}
        className="tbk-relative tbk-h-140 tbk-overflow-hidden tbk-bg-grey-light tbk-pt-7 md:tbk-h-180"
      >
        <div className="tbk-absolute tbk-right-0 tbk-bottom-0 tbk-top-7 tbk-left-0 tbk-flex tbk-w-full tbk-items-end tbk-overflow-hidden tbk-bg-grey-light">
          <picture className="tbk-h-full tbk-grow">
            <source
              media="(min-width: 768px)"
              srcSet={`${hero} 1x, ${hero2x} 2x, ${hero2x} 3x`}
            />
            <source
              srcSet={`${heroMobile} 1x, ${heroMobile2x} 2x, ${heroMobile2x} 3x`}
            />

            <img
              className="tbk-h-full tbk-w-full tbk-object-cover md:tbk-h-full"
              src={hero}
              alt="Hero"
            />
          </picture>
        </div>
        <div className="tbk-container tbk-relative tbk-z-1 tbk-mx-auto tbk-h-full tbk-px-2 tbk-pb-4 lg:tbk-px-32 lg:tbk-pb-7">
          <div className="tbk-flex tbk-h-full tbk-items-end lg:tbk-justify-end">
            <h1 className="tbk-text-extra-bold !tbk-leading-none tbk-text-basic-white md:!tbk-leading-small">
              Our Pillars.
            </h1>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-7 lg:tbk-pt-8 lg:tbk-pt-30 lg:tbk-pb-30">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <p className="tbk-text-title-bold lg:tbk-text-epic-medium tbk-mb-7 tbk-text-center !tbk-font-semibold !tbk-tracking-tighter tbk-text-grey-main">
            Real lifestyle experiences start with real people and authentic,
            immersive engagement. TruBe is leading the well-being movement by
            rising above existing offerings and catering to people's
            post-pandemic needs, wants and desires, from the comfort of their
            home, office, or personal spaces.
          </p>
          <div className="tbk-mb-1 tbk-text-center">
            <TruBeLogoVertical
              width={clientWidth() < theme.responsive.breakpoints.lg ? 74 : 94}
              height={clientWidth() < theme.responsive.breakpoints.lg ? 64 : 80}
              className="tbk-inline-block tbk-text-grey-main"
            />
          </div>
        </div>
      </div>

      <div
        id="fitness"
        className="tbk-relative tbk-flex tbk-h-74 tbk-flex-col tbk-items-center tbk-justify-center lg:tbk-h-[500px]"
      >
        <picture>
          <source
            media="(min-width: 960px)"
            srcSet={`${fitnessBg} 1x, ${fitnessBg2x} 2x`}
          />
          <source srcSet={`${fitnessBgMobile} 1x, ${fitnessBgMobile2x} 2x`} />
          <img
            className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-object-cover"
            src={fitnessBg}
            alt="Fitness"
          />
        </picture>
        <div className="tbk-text-epic-bold lg:tbk-text-accident tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-basic-white">
          Fitness
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-5 tbk-pb-5 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-2 lg:tbk-px-34 lg:tbk-text-center">
          <p className="tbk-text-h3-subtitle lg:tbk-text-bright tbk-mx-auto tbk-text-grey-main lg:tbk-w-2/3">
            TruBe is transforming your fitness routine with in-person,
            on-demand, coaching, and conditioning. Our app provides a seamless
            selection of customizable options, allowing you to tailor your
            workout to your specific needs.
            <br />
            <br />
            Choose your preferred trainer to guide and optimize your progress
            and performance, ensuring a personalized fitness experience like no
            other.
          </p>
        </div>
      </div>

      <div className="tbk-flex tbk-justify-center tbk-overflow-hidden tbk-pt-3 tbk-pb-5 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          {clientWidth() <= theme.responsive.breakpoints.lg ? (
            <div className="tb-slider tb-most-popular-slider">
              <div id={`tb-slide-${1}`} className="tb-slide tbk-mr-2">
                <div className="tbk-mb-2 tbk-grid tbk-grid-cols-homepage-slider-spirit tbk-gap-1 tbk-gap-y-2">
                  {mobileSliderFitnessRows[0].map((data) => (
                    <DisciplineCard key={data.title} {...data} />
                  ))}
                </div>
                <div className="tbk-mb-2 tbk-grid tbk-grid-cols-homepage-slider-spirit tbk-gap-1 tbk-gap-y-2">
                  {mobileSliderFitnessRows[1].map((data) => (
                    <DisciplineCard key={data.title} {...data} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`tbk-grid-cols-homepage-slider tbk-mb-3 tbk-grid tbk-gap-1 tbk-gap-y-2 lg:tbk-mb-7 lg:tbk-grid-cols-3 lg:tbk-gap-x-3 lg:tbk-gap-y-5`}
            >
              <DisciplineCard
                link="/partners/personal-training"
                title="Personal Training"
                images={[disciplineSliderPt, disciplineSliderPt2x]}
              />

              <DisciplineCard
                link="/partners/kickboxing"
                title="Kickboxing"
                images={[
                  disciplineSliderKickboxing,
                  disciplineSliderKickboxing2x,
                ]}
              />

              <DisciplineCard
                link="/partners/pilates"
                title="Pilates"
                images={[disciplineSliderPilates, disciplineSliderPilates2x]}
              />

              <DisciplineCard
                link="/partners/prepost-natal"
                title="Pre/ Post Natal"
                images={[
                  disciplineSliderPrePostNatal,
                  disciplineSliderPrePostNatal2x,
                ]}
              />

              <DisciplineCard
                link="/partners/ballet-fit"
                title="Ballet Fit"
                images={[
                  disciplineSliderBalletFit,
                  disciplineSliderBalletFit2x,
                ]}
              />

              <DisciplineCard
                link="/partners/boxing"
                title="Boxing"
                images={[disciplineSliderBoxing, disciplineSliderBoxing2x]}
              />
            </div>
          )}

          <div className="tbk-text-center">
            <Link to="/partners/fitness">
              <Button
                color="coral"
                className="tbk-mb-3 tbk-inline-block lg:tbk-w-88"
                // endIcon={}
              >
                <span className="tbk-px-3">Explore More Fitness</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div
        id="spirit"
        className="tbk-relative tbk-flex tbk-h-74 tbk-flex-col tbk-items-center tbk-justify-center lg:tbk-h-[500px]"
      >
        <picture>
          <source
            media="(min-width: 960px)"
            srcSet={`${spiritBg} 1x, ${spiritBg2x} 2x`}
          />
          <source srcSet={`${spiritBgMobile} 1x, ${spiritBgMobile2x} 2x`} />
          <img
            className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-object-cover"
            src={spiritBg}
            alt="Spirit"
          />
        </picture>
        <div className="tbk-text-epic-bold lg:tbk-text-accident tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-basic-white">
          Spirit
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-5 tbk-pb-5 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-2 lg:tbk-px-34 lg:tbk-text-center">
          <p className="tbk-text-h3-subtitle lg:tbk-text-bright tbk-mx-auto tbk-text-grey-main lg:tbk-w-2/3">
            TruBe nurtures your healthy mind by providing a range of experiences
            that promote positive thoughts, gratitude, and joy, ultimately
            reducing stress and anxiety.
            <br />
            <br />
            By connecting you with a dedicated coach, we empower your
            spirituality, guiding you towards a calmer and happier existence.
          </p>
        </div>
      </div>

      <div className="tbk-flex tbk-justify-center tbk-overflow-hidden tbk-pt-3 tbk-pb-5 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          {clientWidth() <= theme.responsive.breakpoints.lg ? (
            <div className="tb-slider tb-most-popular-slider">
              <div id={`tb-slide-${1}`} className="tb-slide tbk-mr-2">
                <div className="tbk-mb-2 tbk-grid tbk-grid-cols-homepage-slider-spirit tbk-gap-1 tbk-gap-y-2">
                  {mobileSliderSpiritRows[0].map((data) => (
                    <DisciplineCard key={data.title} {...data} />
                  ))}
                </div>
                <div className="tbk-mb-2 tbk-grid tbk-grid-cols-homepage-slider-spirit tbk-gap-1 tbk-gap-y-2">
                  {mobileSliderSpiritRows[1].map((data) => (
                    <DisciplineCard key={data.title} {...data} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`tbk-grid-cols-homepage-slider tbk-mb-2 tbk-grid tbk-gap-1 tbk-gap-y-2 lg:tbk-mb-7 lg:tbk-grid-cols-3 lg:tbk-gap-x-3 lg:tbk-gap-y-5`}
            >
              <DisciplineCard
                link="/partners/yoga"
                title="Yoga"
                images={[disciplineSliderYoga, disciplineSliderYoga2x]}
              />

              <DisciplineCard
                link="/partners/meditation"
                title="Meditation"
                images={[
                  disciplineSliderMeditation,
                  disciplineSliderMeditation2x,
                ]}
              />

              <DisciplineCard
                link="/partners/sound-baths"
                title="Sound Baths"
                images={[
                  disciplineSliderSoundBaths,
                  disciplineSliderSoundBaths2x,
                ]}
              />

              <DisciplineCard
                link="/partners/lifestyle"
                title="Lifestyle"
                images={[
                  disciplineSliderLifestyle,
                  disciplineSliderLifestyle2x,
                ]}
              />

              <DisciplineCard
                link="/partners/stress-management"
                title="Stress Management"
                images={[
                  disciplineSliderStressManagement,
                  disciplineSliderStressManagement2x,
                ]}
              />

              <DisciplineCard
                link="/partners/motivation"
                title="Motivation"
                images={[
                  disciplineSliderMotivation,
                  disciplineSliderMotivation2x,
                ]}
              />
            </div>
          )}

          <div className="tbk-text-center">
            <Link to="/partners/spirit">
              <Button
                color="coral"
                className="tbk-mb-3 tbk-inline-block lg:tbk-w-88"
                // endIcon={}
              >
                <span className="tbk-px-3">Explore More Spirit</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div
        id="passion"
        className="tbk-relative tbk-flex tbk-h-74 tbk-flex-col tbk-items-center tbk-justify-center lg:tbk-h-[500px]"
      >
        <picture>
          <source
            media="(min-width: 960px)"
            srcSet={`${passionBg} 1x, ${passionBg2x} 2x`}
          />
          <source srcSet={`${passionBgMobile} 1x, ${passionBgMobile2x} 2x`} />
          <img
            className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-object-cover"
            src={passionBg}
            alt="Passion"
          />
        </picture>

        <div className="tbk-text-epic-bold lg:tbk-text-accident tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-basic-white">
          Passion
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-5 tbk-pb-5 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-2 lg:tbk-px-34 lg:tbk-text-center">
          <p className="tbk-text-h3-subtitle lg:tbk-text-bright tbk-mx-auto tbk-text-grey-main lg:tbk-w-2/3">
            TruBe is broadening its range of well-being coaching to include
            popular activities and beloved American sports, bringing together
            fun and fitness for enhanced physical health and mental wellness.
            <br />
            <br />
            By regularly engaging in the sports and activities you love, you'll
            experience increased focus, commitment, and enjoyment, unlocking the
            full potential of your well-being journey.
          </p>
        </div>
      </div>

      <div className="tbk-flex tbk-justify-center tbk-overflow-hidden tbk-pt-3 tbk-pb-5 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          {clientWidth() <= theme.responsive.breakpoints.lg ? (
            <div className="tb-slider tb-most-popular-slider">
              <div id={`tb-slide-${1}`} className="tb-slide tbk-mr-2">
                <div className="tbk-mb-2 tbk-grid tbk-grid-cols-homepage-slider-spirit tbk-gap-1 tbk-gap-y-2">
                  {mobileSliderPassionRows[0].map((data) => (
                    <DisciplineCard key={data.title} hideIcon {...data} />
                  ))}
                </div>
                <div className="tbk-mb-2 tbk-grid tbk-grid-cols-homepage-slider-spirit tbk-gap-1 tbk-gap-y-2">
                  {mobileSliderPassionRows[1].map((data) => (
                    <DisciplineCard key={data.title} hideIcon {...data} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`tbk-grid-cols-homepage-slider tbk-mb-2 tbk-grid tbk-gap-1 tbk-gap-y-2 lg:tbk-mb-7 lg:tbk-grid-cols-3 lg:tbk-gap-x-3 lg:tbk-gap-y-5`}
            >
              <DisciplineCard
                // link="/partners/tennis"
                title="Tennis"
                hideIcon
                images={[disciplineCardTennis, disciplineCardTennis2x]}
              />

              <DisciplineCard
                // link="/partners/golf"
                title="Golf"
                hideIcon
                images={[disciplineCardGolf, disciplineCardGolf2x]}
              />

              <DisciplineCard
                // link="/partners/soccer"
                title="Soccer"
                hideIcon
                images={[disciplineCardSoccer, disciplineCardSoccer2x]}
              />

              <DisciplineCard
                // link="/partners/basketball"
                title="Basketball"
                hideIcon
                images={[disciplineCardBasketball, disciplineCardBasketball2x]}
              />

              <DisciplineCard
                // link="/partners/pickleball"
                title="Pickleball"
                hideIcon
                images={[disciplineCardPickleball, disciplineCardPickleball2x]}
              />

              <DisciplineCard
                // link="/partners/baseball"
                title="Baseball"
                hideIcon
                images={[disciplineCardBaseball, disciplineCardBaseball2x]}
              />
            </div>
          )}

          <div className="tbk-text-center">
            <Link to="/partners/spirit">
              <Button
                color="coral"
                disabled
                className="tbk-mb-3 tbk-inline-block lg:tbk-w-88"
                // endIcon={}
              >
                <span className="tbk-px-3">Coming Soon!</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div
        id="body"
        className="tbk-relative tbk-flex tbk-h-74 tbk-flex-col tbk-items-center tbk-justify-center lg:tbk-h-[500px]"
      >
        <picture>
          <source
            media="(min-width: 960px)"
            srcSet={`${bodyBg} 1x, ${bodyBg2x} 2x`}
          />
          <source srcSet={`${bodyBgMobile} 1x, ${bodyBgMobile2x} 2x`} />
          <img
            className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-object-cover"
            src={bodyBg}
            alt="Body"
          />
        </picture>
        <div className="tbk-text-epic-bold lg:tbk-text-accident tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-basic-white">
          Body
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-5 tbk-pb-5 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-2 lg:tbk-px-34 lg:tbk-text-center">
          <p className="tbk-text-h3-subtitle lg:tbk-text-bright tbk-mx-auto tbk-text-grey-main lg:tbk-w-2/3">
            Experience the transformative power of a healthy body. Discover the
            path to vibrant well-being through nourishing your body with a
            wholesome diet, indulging in nutritious foods and beverages, and
            embracing regular exercise.
            <br />
            <br />
            We are dedicated to connecting you with specialized coaches in
            nutrition and dietetics to enhance your body's health and well-being
            through personalized meal plans and expert guidance. Together, we
            will help you discover a healthier, happier you.
          </p>
        </div>
      </div>

      <div className="tbk-flex tbk-justify-center tbk-overflow-hidden tbk-pt-3 tbk-pb-5 lg:tbk-pt-8 lg:tbk-pb-34">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          {clientWidth() <= theme.responsive.breakpoints.lg ? (
            <div className="tb-slider tb-most-popular-slider">
              <div id={`tb-slide-${1}`} className="tb-slide tbk-mr-2">
                <div className="tbk-mb-2 tbk-grid tbk-grid-cols-homepage-slider-spirit tbk-gap-1 tbk-gap-y-2">
                  {mobileSliderBodyRows[0].map((data) => (
                    <DisciplineCard key={data.title} hideIcon {...data} />
                  ))}
                </div>
                <div className="tbk-mb-2 tbk-grid tbk-grid-cols-homepage-slider-spirit tbk-gap-1 tbk-gap-y-2">
                  {mobileSliderBodyRows[1].map((data) => (
                    <DisciplineCard key={data.title} hideIcon {...data} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`tbk-grid-cols-homepage-slider tbk-mb-2 tbk-grid tbk-gap-1 tbk-gap-y-2 lg:tbk-mb-7 lg:tbk-grid-cols-3 lg:tbk-gap-x-3 lg:tbk-gap-y-5`}
            >
              <DisciplineCard
                // link="/partners/vitamins"
                title="Vitamins"
                hideIcon
                images={[pillarsVitamins, pillarsVitamins2x]}
              />

              <DisciplineCard
                // link="/partners/supplements"
                title="Supplements"
                hideIcon
                images={[pillarsSliderSupplements, pillarsSliderSupplements2x]}
              />

              <DisciplineCard
                // link="/partners/iv-infusion"
                title="IV Infusion"
                hideIcon
                images={[pillarsIVInfusion, pillarsIVInfusion2x]}
              />

              <DisciplineCard
                // link="/partners/nutrition"
                title="Nutrition"
                hideIcon
                images={[pillarsNutrition, pillarsNutrition2x]}
              />

              <DisciplineCard
                // link="/partners/meal-planning"
                title="Meal Planning"
                hideIcon
                images={[pillarsMealPlanning, pillarsMealPlanning2x]}
              />

              <DisciplineCard
                // link="/partners/detox-and-cleanses"
                title="Detox & Cleanses"
                hideIcon
                images={[pillarsDetoxAndCleanses, pillarsDetoxAndCleanses2x]}
              />
            </div>
          )}

          <div className="tbk-text-center">
            <Link to="/partners/spirit">
              <Button
                color="coral"
                disabled
                className="tbk-mb-3 tbk-inline-block lg:tbk-w-88"
                // endIcon={}
              >
                <span className="tbk-px-3">Coming Soon!</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
